import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
    formatTooltipData,
} from "./utils/filterChartData";

export function DebtServiceCoverage({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const filteredData = filterNullValuesFromChartData(data || []);
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Debt Service Coverage"
            description="Highly volatile EBITDA, frequent negative and erratic debt service coverage ratios, and unpredictable runways indicate inconsistent operating performance and significant credit risk despite occasional strong months."
            isCondensed={isCondensed}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtServiceCoverageRatio",
                        yName: "Debt Service Coverage Ratio",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.debtServiceCoverageRatio;

                                return {
                                    heading: `${month}`,
                                    title: "Debt Service Coverage Ratio",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt Service Coverage Ratio",
                                            value:
                                                ratioValue === Infinity
                                                    ? "∞x"
                                                    : `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "EBITDA",
                                            value: formatCurrency(datum.ebitda),
                                        },
                                        {
                                            label: "Interest Expense",
                                            value: formatCurrency(
                                                datum.interestExpense,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Runway",
                                            value:
                                                datum.runway === Infinity
                                                    ? "∞ months"
                                                    : `${datum.runway.toFixed(1)} months`,
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtServiceCoverageRatioFromRemittanceReport",
                        yName: "Debt Service Coverage Ratio (Remittance Report)",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.debtServiceCoverageRatioFromRemittanceReport;

                                return {
                                    heading: `${month}`,
                                    title: "Debt Service Coverage Ratio (Remittance Report)",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt Service Coverage Ratio (Remittance Report)",
                                            value:
                                                ratioValue === Infinity
                                                    ? "∞x"
                                                    : ratioValue === -Infinity
                                                      ? "-∞x"
                                                      : `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "EBITDA",
                                            value: formatCurrency(datum.ebitda),
                                        },
                                        {
                                            label: "Interest and Fees",
                                            value: formatCurrency(
                                                datum.interestAndFees,
                                            ),
                                        },
                                        {
                                            label: "Cash",
                                            value: formatCurrency(datum.cash),
                                        },
                                        {
                                            label: "Runway",
                                            value:
                                                datum.runway === Infinity
                                                    ? "∞ months"
                                                    : `${datum.runway.toFixed(1)} months`,
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        type: "number",
                        position: "right",
                        label: {
                            formatter: (params) => {
                                if (params.value === Infinity) return "∞x";
                                if (params.value === -Infinity) return "-∞x";
                                return `${params.value.toFixed(1)}x`;
                            },
                        },
                        keys: [
                            "debtServiceCoverageRatio",
                            "debtServiceCoverageRatioFromRemittanceReport",
                        ],
                    },
                ],
            }}
        />
    );
}
